import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserName } from "@/components/badges/UserName";
import { Trophy, Medal, Award, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { getStorageUrl } from "@/lib/storage";

interface ConnectionLeader {
  id: string;
  full_name: string;
  avatar_url: string | null;
  role: string;
  connectionCount: number;
  completedCalls: number;
}

export function ConnectionLeaderboard() {
  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ["connection_leaderboard"],
    queryFn: async () => {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role");

      if (profilesError) throw profilesError;
      if (!profiles || profiles.length === 0) return [];

      const userIds = profiles.map((p) => p.id);

      // Fetch all accepted connections
      const { data: acceptedConnections } = await supabase
        .from("connections")
        .select("requester_id, recipient_id")
        .eq("status", "accepted");

      // Fetch all completed connections (mentorship_completed_at is not null)
      const { data: completedConnections } = await supabase
        .from("connections")
        .select("requester_id, recipient_id")
        .not("mentorship_completed_at", "is", null);

      // Calculate connection counts per user
      const userScores: ConnectionLeader[] = profiles.map((profile) => {
        const userId = profile.id;

        // Count accepted connections where user is either requester or recipient
        const acceptedCount =
          acceptedConnections?.filter(
            (c) => c.requester_id === userId || c.recipient_id === userId
          ).length || 0;

        // Count completed calls
        const completedCount =
          completedConnections?.filter(
            (c) => c.requester_id === userId || c.recipient_id === userId
          ).length || 0;

        return {
          id: userId,
          full_name: profile.full_name,
          avatar_url: profile.avatar_url,
          role: profile.role,
          connectionCount: acceptedCount + completedCount,
          completedCalls: completedCount,
        };
      });

      // Sort by connection count and return top 10
      return userScores
        .filter((u) => u.connectionCount > 0)
        .sort((a, b) => b.connectionCount - a.connectionCount)
        .slice(0, 10);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return (
          <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-muted-foreground">
            {rank}
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Top Connectors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Top Connectors
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {leaderboard.map((user, index) => (
            <div
              key={user.id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0">{getRankIcon(index + 1)}</div>
              <Avatar className="h-8 w-8">
                <AvatarImage src={getStorageUrl("avatars", user.avatar_url) || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {user.full_name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate"><UserName userId={user.id} name={user.full_name} /></p>
                <Badge variant="outline" className="text-xs capitalize">
                  {user.role}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-primary">{user.connectionCount}</p>
                <p className="text-xs text-muted-foreground">connections</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
